/**
 * Phase 195.c — Hybrid Honors Hub Sections
 *
 * Three industry-standard zones rendered above the existing champions grid
 * on the public Wall of Champions page:
 *
 *   1. Stat Hub             — 4 live aggregates (tournaments / podiums /
 *                              win-rate / pro signings)
 *   2. Trophy Cabinet       — auto-classified Gold/Silver podium card grid
 *   3. Progression Ladder   — alumni timeline with destination clubs
 *
 * Data: GET /api/academies/public/{slug}/honors-hub (regex-aggregated from
 * `students.wall_milestone` — Phase 195.c. No new collections.)
 *
 * Renders nothing if both `trophies` and `progressions` are empty (keeps the
 * legacy champions roster clean for academies with no curated milestones yet).
 */
import { useEffect, useState } from 'react';
import {
  Trophy, Medal, Target, TrendingUp, Users, Award, ShieldCheck, Sparkles,
} from 'lucide-react';
import { BACKEND_URL } from '@/lib/api';
import HonorsSnapshotShare from './HonorsSnapshotShare';

export default function HonorsHubSections({ slug }) {
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${BACKEND_URL}/api/academies/public/${encodeURIComponent(slug)}/honors-hub`,
        );
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setHub(data);
      } catch (_) { /* silent — section just doesn't render */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading || !hub) return null;

  const { academy, stats, trophies = [], progressions = [] } = hub;

  // Render nothing if zero data — original champions grid carries the page
  if (trophies.length === 0 && progressions.length === 0) return null;

  return (
    <section
      className="relative bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900 text-white py-12 px-4 sm:px-6 border-y border-slate-900"
      data-testid="honors-hub-section"
    >
      {/* Cinematic amber bloom */}
      <div className="pointer-events-none absolute inset-0 opacity-50">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[560px] h-[400px] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(ellipse, rgba(251,191,36,0.10) 0%, transparent 65%)' }}
        />
      </div>

      <div className="relative max-w-5xl mx-auto space-y-12">
        {/* Section eyebrow */}
        <div className="text-center">
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em]"
            style={{
              background: 'rgba(251,191,36,0.10)',
              border: '1px solid rgba(251,191,36,0.32)',
              color: '#fbbf24',
            }}
          >
            <Sparkles className="w-3 h-3" /> Honors Hub · Live Aggregate
          </div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold mt-3 tracking-tight">
            The receipts.
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-2 max-w-md mx-auto leading-relaxed">
            Verified milestones, podium finishes, and professional pathway signings — auto-aggregated from curated athlete records.
          </p>
        </div>

        {/* Phase 195.d — Share Honors Snapshot (1200×630 PNG via Web Share / download) */}
        <HonorsSnapshotShare
          academy={academy}
          stats={stats}
          trophies={trophies}
          progressions={progressions}
        />

        {/* 1. Stat Hub */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="honors-stat-hub">
          <StatTile
            icon={Trophy}
            label="Tournaments Logged"
            value={stats.total_tournaments}
            accent="amber"
            testId="honors-stat-tournaments"
          />
          <StatTile
            icon={Target}
            label="Podium Finishes"
            value={`${stats.podiums} ${stats.podiums === 1 ? 'Medal' : 'Medals'}`}
            accent="emerald"
            testId="honors-stat-podiums"
          />
          <StatTile
            icon={TrendingUp}
            label="Achievement Velocity"
            value={stats.win_rate || '—'}
            accent="indigo"
            testId="honors-stat-velocity"
          />
          <StatTile
            icon={Users}
            label="Pro Pathway Signings"
            value={`${stats.pro_promotions} ${stats.pro_promotions === 1 ? 'Athlete' : 'Athletes'}`}
            accent="rose"
            testId="honors-stat-signings"
          />
        </div>

        {/* 2. Trophy Cabinet */}
        {trophies.length > 0 && (
          <div className="space-y-5" data-testid="honors-trophy-cabinet">
            <div className="border-b border-slate-800 pb-2 flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-display font-bold flex items-center gap-2">
                <Medal className="w-4 h-4 text-amber-400" /> Verified Accolades
              </h3>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                {trophies.length} {trophies.length === 1 ? 'Record' : 'Records'}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trophies.map((t, i) => (
                <article
                  key={i}
                  className="relative bg-slate-900/30 border border-slate-800/60 hover:border-amber-500/30 rounded-2xl p-5 transition-all group"
                  data-testid="honors-trophy-card"
                >
                  {t.year && (
                    <span className="absolute top-3 right-3 text-[10px] font-bold text-slate-500 bg-slate-950 px-2 py-0.5 rounded-md border border-slate-800">
                      {t.year}
                    </span>
                  )}
                  <div className="text-2xl mb-2">
                    {t.rank === 'Gold' ? '🥇' : t.rank === 'Silver' ? '🥈' : '🏆'}
                  </div>
                  <h4 className="text-sm font-bold text-white tracking-tight leading-snug pr-10">
                    {t.tournament_name}
                  </h4>
                  <p className="text-[11px] text-amber-400 font-semibold mt-1.5">
                    {t.division} · {t.rank}
                  </p>
                  <p className="text-[11px] text-slate-400 leading-relaxed mt-3">
                    {t.summary}
                  </p>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* 3. Progression Ladder (Alumni Timeline) */}
        {progressions.length > 0 && (
          <div className="space-y-5" data-testid="honors-progression-ladder">
            <div className="border-b border-slate-800 pb-2 flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-display font-bold flex items-center gap-2">
                <Award className="w-4 h-4 text-indigo-400" /> Professional Scouting & Elite Signings
              </h3>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                {progressions.length} {progressions.length === 1 ? 'Signing' : 'Signings'}
              </span>
            </div>
            <div className="relative border-l-2 border-slate-800 ml-3 sm:ml-4 space-y-6 py-2">
              {progressions.map((p, i) => (
                <div key={i} className="relative pl-6 sm:pl-8 group" data-testid="honors-progression-row">
                  <div className="absolute left-0 top-3 -translate-x-1/2 w-3 h-3 bg-slate-950 border-2 border-indigo-500 rounded-full group-hover:bg-indigo-400 transition" />
                  <div className="bg-slate-900/30 border border-slate-800/60 hover:border-indigo-500/30 p-4 rounded-xl transition max-w-2xl">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <h4 className="text-sm font-bold text-white tracking-tight">
                          {p.athlete_name}
                        </h4>
                        <p className="text-[11px] font-bold text-indigo-400 uppercase tracking-wide mt-0.5">
                          → {p.destination_club}
                        </p>
                      </div>
                      {p.signing_date && (
                        <span className="text-[10px] font-bold text-slate-500 bg-slate-950 border border-slate-800 px-2 py-0.5 rounded shrink-0">
                          {p.signing_date}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                      {p.notes}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Data source footer */}
        <div className="text-center pt-2">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-600 font-bold">
            <ShieldCheck className="w-3 h-3" />
            Auto-aggregated · Out-Play Honors Engine
          </div>
        </div>
      </div>
    </section>
  );
}

const ACCENT_MAP = {
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-400' },
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-400' },
  rose: { bg: 'bg-rose-500/10', text: 'text-rose-400' },
};

function StatTile({ icon: Icon, label, value, accent, testId }) {
  const a = ACCENT_MAP[accent] || ACCENT_MAP.amber;
  return (
    <div
      className="bg-slate-900/40 border border-slate-800/60 rounded-2xl p-4 sm:p-5 flex items-center gap-3"
      data-testid={testId}
    >
      <div className={`w-9 h-9 sm:w-10 sm:h-10 ${a.bg} rounded-xl flex items-center justify-center ${a.text} shrink-0`}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider text-slate-500 leading-tight">
          {label}
        </p>
        <h3 className="text-base sm:text-lg font-black text-white truncate">{value}</h3>
      </div>
    </div>
  );
}
